import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, getToken } from "../api";
import StickyHead from "../StickyHead";

/* 개발 문의 — 홈페이지 /contact 로 접수된 개발 의뢰와 공동개발(Partnership) 문의.
   2026-11 에 따로 받던 프로젝트 제안(/apply)을 합쳤다. 옮겨 온 제안은 예전 접수번호(P-1 …)를 함께 보인다.
   'Partnership 제안' · '일반개발 제안' 은 공동개발 심사 결과라 공동개발 문의에만 버튼을 보인다. */
const PARTNER_TYPE = "공동개발(Partnership) 문의"; // 백엔드 app/inquiries.py PARTNER_TYPE

const STATUSES = [
  ["NEW", "신규"],
  ["REVIEWING", "검토중"],
  ["REPLIED", "회신"],
  ["PARTNERSHIP", "Partnership 제안"],
  ["GENERAL", "일반개발 제안"],
  ["IN_PROGRESS", "진행"],
  ["HOLD", "보류"],
  ["CLOSED", "종료"],
];
const STATUS_LABEL = Object.fromEntries(STATUSES);
const REVIEW_ONLY = ["PARTNERSHIP", "GENERAL"];
const DONE = ["HOLD", "CLOSED"];

/* 백엔드가 UTC 로 기록하므로(다른 SSTOA 테이블과 동일) 표시할 때 KST 로 바꾼다 */
function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(/[Zz+]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "medium", timeStyle: "short" });
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

/* 목록 한 줄 — 누르면 목록을 감추고 그 문의의 상세만 보인다 */
function ListRow({ item, onOpen }) {
  const partner = item.inquiry_type === PARTNER_TYPE;
  return (
    <tr style={{ cursor: "pointer" }} onClick={() => onOpen(item)}>
      <td>
        <span className={`sstoa-tag ${DONE.includes(item.status) ? "sstoa-tag--gray" : ""}`}>
          {STATUS_LABEL[item.status] || item.status}
        </span>
      </td>
      <td style={{ fontWeight: 600 }}>
        {item.company || item.name}
        <div style={{ fontWeight: 400, fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>
          {item.company ? `${item.name} · ` : ""}{item.email}
        </div>
      </td>
      <td>
        {partner ? <span className="sstoa-tag">공동개발 문의</span> : item.inquiry_type}
        {partner && (
          <div style={{ fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>
            {[item.industry, item.sales_channel].filter(Boolean).join(" · ")}
          </div>
        )}
      </td>
      <td>{item.stage}</td>
      <td>{item.budget || "—"}</td>
      <td>{formatWhen(item.created_at)}</td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        {item.attachment_name ? "📎 " : ""}
        {!item.mail_sent && <span className="sstoa-tag sstoa-tag--gray" title="관리자 알림 메일이 발송되지 않았습니다">메일 실패</span>}
      </td>
    </tr>
  );
}

/* 문의 상세 — 목록 대신 화면 전체에 보인다. 오른쪽 위 [닫기]로 목록에 돌아간다 */
function Detail({ item, onChange, onRemove, onClose }) {
  const [memo, setMemo] = useState(item.admin_memo || "");
  const [busy, setBusy] = useState(false);
  const partner = item.inquiry_type === PARTNER_TYPE;

  async function patch(body) {
    setBusy(true);
    try { await onChange(item.id, body); } finally { setBusy(false); }
  }

  /* 첨부는 인증이 필요해 <a href> 로 바로 못 받는다. 토큰을 실어 blob 으로 내려받는다. */
  async function downloadAttachment() {
    setBusy(true);
    try {
      const res = await fetch(`/api/sstoa/inquiries/${item.id}/attachment`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("첨부를 받지 못했습니다.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.attachment_name || `inquiry-${item.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  }

  const subject = partner
    ? `[I.META] 공동개발 문의 검토 결과 (#${item.id})`
    : `[I.META] 개발 문의 회신 (#${item.id})`;
  const buttons = STATUSES.filter(([v]) => partner || !REVIEW_ONLY.includes(v) || item.status === v);

  return (
    <div className="sstoa-panel">
      <div className="sstoa-panel__head">
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span className={`sstoa-tag ${DONE.includes(item.status) ? "sstoa-tag--gray" : ""}`}>
              {STATUS_LABEL[item.status] || item.status}
            </span>
            {partner && <span className="sstoa-tag">공동개발 문의</span>}
            <h2 style={{ margin: 0, fontSize: 18 }}>{item.company || item.name}</h2>
          </div>
          <div style={{ fontSize: 13, color: "var(--s-fg-mute)", marginTop: 4 }}>
            접수 #{item.id}{item.legacy_ref ? ` (예전 접수번호 ${item.legacy_ref})` : ""} · {formatWhen(item.created_at)}
          </div>
        </div>
        <button className="sstoa-btn" onClick={onClose}>닫기</button>
      </div>

      <div className="sstoa-panel--pad">
        <dl className="sstoa-meta" style={{ margin: "0 0 18px" }}>
          <dt>문의자</dt><dd>{item.company ? `${item.company} · ` : ""}{item.name}</dd>
          <dt>이메일</dt><dd><a href={`mailto:${item.email}`}>{item.email}</a></dd>
          <dt>연락처</dt><dd>{item.phone || "—"}</dd>
          <dt>유형</dt><dd>{item.inquiry_type}</dd>
          <dt>단계</dt><dd>{item.stage || "—"}</dd>
          <dt>예산</dt><dd>{item.budget || "—"}</dd>
          <dt>희망 일정</dt><dd>{item.schedule || "—"}</dd>
          <dt>확인메일</dt><dd>{item.ack_sent ? "발송" : "미발송"}{item.mail_sent ? "" : " · 관리자 알림 메일 실패"}</dd>
          {partner && (
            <>
              <dt>사업자 형태</dt><dd>{item.biz_type || "—"}</dd>
              <dt>업종</dt><dd>{item.industry || "—"}</dd>
              <dt>판로</dt><dd>{item.sales_channel || "—"}</dd>
              <dt>정부지원사업</dt><dd>{item.gov_support || "—"}</dd>
              {item.gov_support === "선정됨" && (
                <>
                  <dt>지원사업</dt><dd>{item.gov_program || "—"}</dd>
                  <dt>개발 가능 예산</dt><dd>{item.gov_budget || "—"}</dd>
                  <dt>협약 종료</dt><dd>{item.gov_end_date || "—"}</dd>
                </>
              )}
            </>
          )}
        </dl>

        <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.7, marginBottom: 18, padding: "14px 16px",
                      border: "1px solid var(--s-border)", borderRadius: 8 }}>
          {item.message}
        </div>

        {item.attachment_name && (
          <div style={{ marginBottom: 18 }}>
            <button className="sstoa-btn sstoa-btn--sm" onClick={downloadAttachment} disabled={busy}>
              첨부 내려받기 — {item.attachment_name} {formatSize(item.attachment_size)}
            </button>
          </div>
        )}

        <div className="sstoa-field" style={{ marginBottom: 14 }}>
          <label>{partner ? "심사 메모" : "처리 메모"}</label>
          <textarea
            className="sstoa-textarea" value={memo} disabled={busy}
            onChange={(e) => setMemo(e.target.value)}
          />
          <div className="sstoa-actions" style={{ marginTop: 8 }}>
            <button className="sstoa-btn sstoa-btn--sm" onClick={() => patch({ admin_memo: memo })} disabled={busy}>메모 저장</button>
          </div>
        </div>

        <div className="sstoa-actions">
          <a className="sstoa-btn sstoa-btn--sm" href={`mailto:${item.email}?subject=${encodeURIComponent(subject)}`}>
            메일로 회신
          </a>
          {buttons.map(([v, l]) => (
            <button
              key={v} disabled={busy || item.status === v}
              className={`sstoa-btn sstoa-btn--sm${item.status === v ? " sstoa-btn--primary" : ""}`}
              onClick={() => patch({ status: v })}
            >
              {l}
            </button>
          ))}
          <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => onRemove(item.id)} disabled={busy}>
            삭제
          </button>
        </div>

        {/* 회신 단계 — 업체 등록 없이 이 문의 내용(업체명 · 담당자 · 연락처 · 이메일 · 제목)으로 견적서를 바로 쓴다 */}
        {item.status === "REPLIED" && (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--s-border)", display: "flex",
                        alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "var(--s-fg-mute)" }}>
              업체 등록 없이 이 문의 내용으로 견적서를 작성해 보냅니다.
            </span>
            <Link to={`/sstoa/quotes/new?inquiry=${item.id}`} className="sstoa-btn sstoa-btn--primary">견적서 보내기</Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default function InquiriesPage() {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState(null);
  const [filter, setFilter] = useState("");
  const [onlyPartner, setOnlyPartner] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null); // 상세를 보고 있는 문의 — 있으면 목록 대신 상세만 보인다
  const listY = useRef(0); // 목록으로 돌아올 때 보던 자리로

  async function load(status = filter) {
    setError("");
    try {
      const [list, c] = await Promise.all([
        api.get(`/inquiries${status ? `?status=${status}` : ""}`),
        api.get("/inquiries/counts"),
      ]);
      setRows(list);
      setCounts(c);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    load("");
    // 최초 1회만 전체 목록을 불러온다. 이후 갱신은 onFilter/change/remove 가 직접 호출한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onFilter(s) { setFilter(s); load(s); }

  function openItem(it) {
    listY.current = window.scrollY;
    setError("");
    setOpen(it);
    window.scrollTo(0, 0);
  }

  function closeItem() {
    setOpen(null);
    setError("");
    requestAnimationFrame(() => window.scrollTo(0, listY.current));
  }

  /* 상세는 서버가 돌려준 문의로 바로 바꾼다 — 상태를 바꿔 지금 필터에서 빠져도 상세는 그대로 보인다 */
  async function change(id, patch) {
    try {
      const updated = await api.post(`/inquiries/${id}`, patch);
      if (updated) setOpen((o) => (o && o.id === id ? updated : o));
      await load();
    } catch (err) { setError(err.message); }
  }

  async function remove(id) {
    if (!window.confirm("이 문의를 삭제할까요? 첨부 파일도 함께 지워집니다.")) return;
    try {
      await api.del(`/inquiries/${id}`);
      setOpen(null);
      await load();
    } catch (err) { setError(err.message); }
  }

  const shown = rows && (onlyPartner ? rows.filter((r) => r.inquiry_type === PARTNER_TYPE) : rows);

  if (open) {
    return (
      <div>
        <div style={{ marginBottom: 12 }}>
          <button type="button" className="sstoa-back" style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
            onClick={closeItem}>← 개발 문의 목록</button>
        </div>
        {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}
        <Detail key={open.id} item={open} onChange={change} onRemove={remove} onClose={closeItem} />
      </div>
    );
  }

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        </div>

        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">개발 문의</h1>
            <p className="sstoa-sub">홈페이지 개발 문의 폼으로 접수된 개발 의뢰와 공동개발 문의입니다. 행을 누르면 그 문의의 상세가 열립니다.</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <button className={`sstoa-btn sstoa-btn--sm${filter === "" ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter("")}>
            전체{counts ? ` ${counts.total}` : ""}
          </button>
          {STATUSES.map(([v, l]) => (
            <button key={v} className={`sstoa-btn sstoa-btn--sm${filter === v ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter(v)}>
              {l}{counts ? ` ${counts[v] || 0}` : ""}
            </button>
          ))}
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 8, fontSize: 13.5 }}>
            <input type="checkbox" checked={onlyPartner} onChange={(e) => setOnlyPartner(e.target.checked)} />
            공동개발 문의만
          </label>
        </div>
      </StickyHead>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="sstoa-panel">
        {shown === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : shown.length === 0 ? (
          <div className="sstoa-empty">접수된 문의가 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead>
              <tr>
                <th>상태</th><th>문의자</th><th>유형</th><th>단계</th><th>예산</th><th>접수</th><th></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((it) => (
                <ListRow key={it.id} item={it} onOpen={openItem} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
